import {
  useMutation,
  useQueryClient,
  UseMutationResult,
} from "@tanstack/react-query"
import { AxiosResponse, isAxiosError } from "axios"

import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import { queryKeys } from "api/queryKeys"
import { HandledApiError } from "api/queryClient"
import {
  createProject,
  deleteProject,
  updateProject,
  archiveProject,
  unarchiveProject,
  copyProject,
  moveProject,
  projectMemberAdd,
  projectMemberRemove,
  createProjectFolder,
  deleteProjectFolder,
  renameProjectFolder,
  setProjectFolder,
  CopyHistoryMode,
} from "api/Repos/projects"
import { CreateProjectBody, UpdateProjectBody } from "types/project.types"
import handleSequenceError from "utils/handleSequenceError"
import { failureOf } from "utils/errMsgOf"
import { notify } from "shared/ui/Notifications/notify"

/**
 * React-query mutations for the Projects domain (queries live in
 * `useProjectsQuery.ts`).
 *
 * Shared pattern: the mutationFn gates the response through
 * `handleSequenceError` (which notifies the user on a business error) and
 * throws `HandledApiError` so onError does not notify a second time. Success
 * notifications and cache invalidation run in onSuccess, so call sites just
 * `await mutateAsync(...)` and follow up (navigate, close a modal) — a
 * rejection means the user has already been told what went wrong.
 *
 * Folder mutations and create-project skip notifications: their call sites
 * own context-specific messages (folder paths, custom callbacks). They still
 * invalidate the projects cache.
 */

interface ProjectOwnerArgs {
  id: string
  owner: string
}

interface MemberArgs {
  id: string
  member: string
}

/** Builds a sequence-gated mutationFn + notification pair used by most hooks. */
const notifyTransportError =
  (title: string) =>
  (error: Error): void => {
    if (error instanceof HandledApiError) return
    notify.error(title, error.message)
  }

export const useCreateProjectMutation = (): UseMutationResult<
  AxiosResponse<string>,
  Error,
  CreateProjectBody
> => {
  const getAxios = useAuthedAxios()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (body: CreateProjectBody) =>
      createProject(await getAxios(), body),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.root }),
  })
}

export const useDeleteProjectMutation = (): UseMutationResult<
  AxiosResponse<unknown>,
  Error,
  string
> => {
  const getAxios = useAuthedAxios()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await deleteProject(await getAxios(), id)
      if (!handleSequenceError(res.data, "Error Deleting Project!")) {
        throw new HandledApiError("Sequence error deleting project")
      }
      return res
    },
    onSuccess: () => {
      notify.success(
        "Project Deleted Successfully!",
        "The project has been deleted successfully."
      )
      // Mark projects data stale WITHOUT refetching now: the deleted
      // project's details query is still mounted (callers navigate away
      // right after) and refetching it would 404. Stale queries refetch
      // when their consumers next mount (e.g. the /home lists).
      return queryClient.invalidateQueries({
        queryKey: queryKeys.projects.root,
        refetchType: "none",
      })
    },
    onError: notifyTransportError("Error Deleting Project!"),
  })
}

export const useUpdateProjectMutation = (): UseMutationResult<
  AxiosResponse<unknown>,
  Error,
  UpdateProjectBody
> => {
  const getAxios = useAuthedAxios()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (body: UpdateProjectBody) => {
      const res = await updateProject(await getAxios(), body)
      if (!handleSequenceError(res.data, "Error Updating Project!")) {
        throw new HandledApiError("Sequence error updating project")
      }
      return res
    },
    onSuccess: () => {
      notify.success(
        "Project Updated Successfully!",
        "The project has been updated successfully."
      )
      return queryClient.invalidateQueries({
        queryKey: queryKeys.projects.root,
      })
    },
    onError: notifyTransportError("Error Updating Project!"),
  })
}

export const useArchiveProjectMutation = (): UseMutationResult<
  AxiosResponse<unknown>,
  Error,
  ProjectOwnerArgs
> => {
  const getAxios = useAuthedAxios()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, owner }: ProjectOwnerArgs) => {
      const res = await archiveProject(await getAxios(), id, owner)
      if (!handleSequenceError(res.data, "Error Archiving Project!")) {
        throw new HandledApiError("Sequence error archiving project")
      }
      return res
    },
    onSuccess: () => {
      notify.success(
        "Project Archived Successfully!",
        "The project has been archived successfully."
      )
      return queryClient.invalidateQueries({
        queryKey: queryKeys.projects.root,
      })
    },
    onError: notifyTransportError("Error Archiving Project!"),
  })
}

export const useUnarchiveProjectMutation = (): UseMutationResult<
  AxiosResponse<unknown>,
  Error,
  ProjectOwnerArgs
> => {
  const getAxios = useAuthedAxios()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, owner }: ProjectOwnerArgs) => {
      const res = await unarchiveProject(await getAxios(), id, owner)
      if (!handleSequenceError(res.data, "Error Unarchiving Project!")) {
        throw new HandledApiError("Sequence error unarchiving project")
      }
      return res
    },
    onSuccess: () => {
      notify.success(
        "Project Unarchived Successfully!",
        "The project has been unarchived successfully."
      )
      return queryClient.invalidateQueries({
        queryKey: queryKeys.projects.root,
      })
    },
    onError: notifyTransportError("Error Unarchiving Project!"),
  })
}

/**
 * The new project's id when a wave copy was made but couldn't be added to the tracker (the
 * error answer still names the copy), else null. The copy exists and can be added by hand.
 */
export const copiedNotWaveId = (error: unknown): string | null => {
  if (!isAxiosError(error)) return null
  const body: unknown = error.response?.data
  if (!body || typeof body !== "object") return null
  const id = (body as Record<string, unknown>).projectId
  return typeof id === "string" && id !== "" ? id : null
}

export const useCopyProjectMutation = (): UseMutationResult<
  AxiosResponse<unknown>,
  Error,
  {
    id: string
    owner: string
    copy: string
    name: string
    newOwner: string
    historyMode: CopyHistoryMode
  }
> => {
  const getAxios = useAuthedAxios()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, owner, copy, name, newOwner, historyMode }) => {
      const res = await copyProject(
        await getAxios(),
        id,
        owner,
        copy,
        name,
        newOwner,
        historyMode
      )
      if (!handleSequenceError(res.data, "Error Copying Project!")) {
        throw new HandledApiError("Sequence error copying project")
      }
      return res
    },
    onSuccess: () => {
      notify.success(
        "Project Copied Successfully!",
        "The project has been copied successfully."
      )
      return queryClient.invalidateQueries({
        queryKey: queryKeys.projects.root,
      })
    },
    // A refused copy answers with the server's message. A wave copy that couldn't be added to
    // the tracker still made the copy, so the project list is read again.
    onError: (error) => {
      if (error instanceof HandledApiError) return
      notify.error("Error Copying Project!", failureOf(error))
      if (copiedNotWaveId(error))
        return queryClient.invalidateQueries({ queryKey: queryKeys.projects.root })
    },
  })
}

export const useMoveProjectMutation = (): UseMutationResult<
  AxiosResponse<unknown>,
  Error,
  { id: string; owner: string; newOwner: string }
> => {
  const getAxios = useAuthedAxios()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, owner, newOwner }) => {
      const res = await moveProject(await getAxios(), id, owner, newOwner)
      if (!handleSequenceError(res.data, "Error Moving Project!")) {
        throw new HandledApiError("Sequence error moving project")
      }
      return res
    },
    onSuccess: () => {
      notify.success(
        "Project Moved Successfully!",
        "The project ownership has been transferred successfully."
      )
      return queryClient.invalidateQueries({
        queryKey: queryKeys.projects.root,
      })
    },
    onError: notifyTransportError("Error Moving Project!"),
  })
}

export const useAddProjectMemberMutation = (): UseMutationResult<
  AxiosResponse<unknown>,
  Error,
  MemberArgs
> => {
  const getAxios = useAuthedAxios()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, member }: MemberArgs) => {
      const res = await projectMemberAdd(await getAxios(), id, member)
      if (!handleSequenceError(res.data, "Error Adding Member!")) {
        throw new HandledApiError("Sequence error adding member")
      }
      return res
    },
    onSuccess: (_res, { id, member }) => {
      notify.success(
        "Member Added Successfully!",
        `Member ${member} has been added successfully.`
      )
      return queryClient.invalidateQueries({
        queryKey: queryKeys.projects.members(id),
      })
    },
    onError: notifyTransportError("Error Adding Member!"),
  })
}

export const useRemoveProjectMemberMutation = (): UseMutationResult<
  AxiosResponse<unknown>,
  Error,
  MemberArgs
> => {
  const getAxios = useAuthedAxios()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, member }: MemberArgs) => {
      const res = await projectMemberRemove(await getAxios(), id, member)
      if (!handleSequenceError(res.data, "Error Removing Member!")) {
        throw new HandledApiError("Sequence error removing member")
      }
      return res
    },
    onSuccess: (_res, { id, member }) => {
      notify.success(
        "Member Removed Successfully!",
        `Member ${member} has been removed successfully.`
      )
      return queryClient.invalidateQueries({
        queryKey: queryKeys.projects.members(id),
      })
    },
    onError: notifyTransportError("Error Removing Member!"),
  })
}

/**
 * Folder mutations. No baked-in notifications — the folder modals own
 * context-specific messages and validation. Invalidation covers folders and
 * project lists (folder assignments live in project metadata).
 */

export const useCreateProjectFolderMutation = (): UseMutationResult<
  AxiosResponse<unknown>,
  Error,
  string
> => {
  const getAxios = useAuthedAxios()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (folder: string) =>
      createProjectFolder(await getAxios(), folder),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.root }),
  })
}

export const useDeleteProjectFolderMutation = (): UseMutationResult<
  AxiosResponse<unknown>,
  Error,
  string
> => {
  const getAxios = useAuthedAxios()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (folder: string) =>
      deleteProjectFolder(await getAxios(), folder),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.root }),
  })
}

export const useRenameProjectFolderMutation = (): UseMutationResult<
  AxiosResponse<unknown>,
  Error,
  { oldPath: string; newPath: string }
> => {
  const getAxios = useAuthedAxios()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ oldPath, newPath }) =>
      renameProjectFolder(await getAxios(), oldPath, newPath),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.root }),
  })
}

export const useSetProjectFolderMutation = (): UseMutationResult<
  AxiosResponse<unknown>,
  Error,
  { id: string; owner: string; folder: string }
> => {
  const getAxios = useAuthedAxios()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, owner, folder }) =>
      setProjectFolder(await getAxios(), id, owner, folder),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.root }),
  })
}
